import React, { useEffect, useMemo, useState } from "react";
import Swal from "sweetalert2";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import CloudDownloadOutlinedIcon from "@mui/icons-material/CloudDownloadOutlined";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import SearchIcon from "@mui/icons-material/Search";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useHistory, useLocation } from "react-router-dom";
import request from "../../../Helpers/API";
import PlantillaScreen from "../../helpers/PlantillaScreen";
import { CRM_ModalActualizarDocumento } from "./CRM_ModalActualizarDocumento";

// idcliente sin seleccionar puede llegar en varias formas equivalentes desde location.state
const esIdClienteInvalido = (idcliente) =>
    idcliente === undefined ||
    idcliente === null ||
    idcliente === 0 ||
    idcliente === "0" ||
    idcliente === "" ||
    idcliente === "-1";

// Deriva texto y modificador de clase del badge de estatus a partir de vigencia/estado
const obtenerEstadoDocumento = (item) => {
    if (item.vigencia === "-") return { texto: "-", modificador: "" };
    if (item.estado >= 1) return { texto: "Vencido", modificador: "vencido" };
    if (item.estado <= 0 && item.estado >= -30) return { texto: "Por vencer", modificador: "porvencer" };
    return { texto: "Vigente", modificador: "vigente" };
};

// Agrupa el estatus para KPIs/filtros: sin documento cargado cuenta igual que vencido
const obtenerGrupoEstado = (item) => {
    const { modificador } = obtenerEstadoDocumento(item);
    return modificador === "vigente" || modificador === "porvencer" ? modificador : "vencido";
};

const capitalizeWords = (str) =>
    str ? str.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase()) : "";

// Filtros de estatus disponibles sobre el listado de documentos
const FILTROS_ESTADO = [
    { valor: "todos", etiqueta: "Todos" },
    { valor: "vigente", etiqueta: "Vigentes" },
    { valor: "porvencer", etiqueta: "Por vencer" },
    { valor: "vencido", etiqueta: "Vencidos" },
];

export const CRM_ExpedienteDigital = () => {
    const history = useHistory();
    const location = useLocation();

    const idcliente = location.state?.idcliente;
    const linea = location.state?.linea;
    const cliente = location.state?.cliente;

    // Título de la plantilla: línea de crédito + cliente seleccionado
    const lineaTexto = linea === "J" ? "JDF" : linea === "O" ? "MHUSA" : "DIGITAL";
    const titulo = `EXPEDIENTE ${lineaTexto}${cliente ? `: ${cliente}` : ""}`;

    const [documentos, setDocumentos] = useState([]);
    const [busqueda, setBusqueda] = useState("");
    const [filtroEstado, setFiltroEstado] = useState("todos");
    const [documentoModal, setDocumentoModal] = useState(null);

    const cargarDocumentos = () => {
        if (esIdClienteInvalido(idcliente)) return;
        request
            .get(`ClienteDocumentacion/ListadoPorLineaCredito?idcliente=${idcliente}&linea=${linea}`)
            .then((result) => setDocumentos(result || []))
            .catch((error) => Swal.fire("Clientes", JSON.stringify(error), "error"));
    };

    useEffect(() => {
        cargarDocumentos();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [idcliente]);

    // El endpoint solo trae el listado; los KPIs y el progreso se calculan aquí a partir de vigencia/estado de cada documento
    const resumen = useMemo(() => {
        let vigentes = 0;
        let porVencer = 0;
        let vencidos = 0;
        let actualizados = 0;
        documentos.forEach((item) => {
            const grupo = obtenerGrupoEstado(item);
            if (grupo === "vigente") vigentes += 1;
            else if (grupo === "porvencer") porVencer += 1;
            else vencidos += 1;
            if (grupo === "vigente" || grupo === "porvencer") actualizados += 1;
        });
        const total = documentos.length;
        return {
            total,
            vigentes,
            porVencer,
            vencidos,
            actualizados,
            porcentaje: total > 0 ? Math.round((actualizados / total) * 100) : 0,
        };
    }, [documentos]);

    // Documentos visibles según búsqueda/filtro; los KPIs de arriba siempre reflejan el total real
    const documentosFiltrados = useMemo(() => {
        const texto = busqueda.trim().toLowerCase();
        return documentos.filter((item) => {
            const coincideEstado = filtroEstado === "todos" || obtenerGrupoEstado(item) === filtroEstado;
            const coincideBusqueda = texto === "" || item.documento?.toLowerCase().includes(texto);
            return coincideEstado && coincideBusqueda;
        });
    }, [documentos, busqueda, filtroEstado]);

    const onDescargar = (idclientedocumento) => {
        request
            .get(`ClienteDocumentacion/BuscarID?idclientedocumento=${idclientedocumento}`)
            .then((result) => {
                const downloadLink = document.createElement("a");
                downloadLink.href = result.documento;
                downloadLink.download = `${result.nombre}.${result.extension}`;
                downloadLink.click();
            })
            .catch((error) => Swal.fire("Clientes", JSON.stringify(error), "error"));
    };

    const onRegresar = () => history.push("/DashClientes/Main", { idcliente });

    if (esIdClienteInvalido(idcliente)) {
        return (
            <PlantillaScreen titulo={titulo}>
                <p className="crm-expediente-digital-sin-cliente">Favor de seleccionar un cliente.</p>
            </PlantillaScreen>
        );
    }

    return (
        <>
            <PlantillaScreen titulo={titulo} className={"crm-expediente-digital-main-container"}>
                <div className="crm-expediente-digital-boton-regresar-container">
                    <button
                        className="crm-expediente-digital-boton-regresar "
                        onClick={() => onRegresar()}
                    >
                        <ArrowBackIcon />
                        Regresar
                    </button>
                </div>

                <div className="crm-expediente-digital">
                    {documentos.length > 0 && (
                        <>
                            <div className="crm-expediente-digital-kpis">
                                <div className="crm-expediente-digital-kpi">
                                    <span className="crm-expediente-digital-kpi-num">{resumen.total}</span>
                                    <span className="crm-expediente-digital-kpi-lbl">Totales</span>
                                </div>
                                <div className="crm-expediente-digital-kpi crm-expediente-digital-kpi-vigente">
                                    <span className="crm-expediente-digital-kpi-num">{resumen.vigentes}</span>
                                    <span className="crm-expediente-digital-kpi-lbl">Vigentes</span>
                                </div>
                                <div className="crm-expediente-digital-kpi crm-expediente-digital-kpi-porvencer">
                                    <span className="crm-expediente-digital-kpi-num">{resumen.porVencer}</span>
                                    <span className="crm-expediente-digital-kpi-lbl">Por vencer</span>
                                </div>
                                <div className="crm-expediente-digital-kpi crm-expediente-digital-kpi-vencido">
                                    <span className="crm-expediente-digital-kpi-num">{resumen.vencidos}</span>
                                    <span className="crm-expediente-digital-kpi-lbl">Vencidos</span>
                                </div>
                            </div>

                            <div className="crm-expediente-digital-progreso">
                                <div className="crm-expediente-digital-progreso-header">
                                    <span className="crm-expediente-digital-progreso-desc">
                                        {resumen.actualizados} documentos actualizados de {resumen.total}
                                    </span>
                                    <span className="crm-expediente-digital-progreso-porc">{resumen.porcentaje}%</span>
                                </div>
                                <div className="crm-expediente-digital-progreso-track">
                                    <div
                                        className="crm-expediente-digital-progreso-fill"
                                        style={{ width: `${resumen.porcentaje}%` }}
                                    />
                                </div>
                            </div>

                            <div className="crm-expediente-digital-filtros">
                                <div className="crm-expediente-digital-buscar">
                                    <SearchIcon />
                                    <input
                                        type="text"
                                        placeholder="Buscar documento..."
                                        value={busqueda}
                                        onChange={(e) => setBusqueda(e.target.value)}
                                    />
                                </div>
                                <div className="crm-expediente-digital-chips">
                                    {FILTROS_ESTADO.map((filtro) => (
                                        <button
                                            key={filtro.valor}
                                            type="button"
                                            className={`crm-expediente-digital-chip${filtroEstado === filtro.valor ? " activo" : ""}`}
                                            onClick={() => setFiltroEstado(filtro.valor)}
                                        >
                                            {filtro.etiqueta}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </>
                    )}

                    <div className="crm-expediente-digital-lista scroll-4">
                        {documentos.length === 0 ? (
                            <p className="crm-expediente-digital-sin-documentos">
                                Este cliente no tiene documentos registrados en este expediente.
                            </p>
                        ) : documentosFiltrados.length === 0 ? (
                            <p className="crm-expediente-digital-sin-documentos">
                                No hay documentos que coincidan con la búsqueda.
                            </p>
                        ) : (
                            documentosFiltrados.map((item) => {
                                const estado = obtenerEstadoDocumento(item);
                                const tieneFecha = item.vigencia && item.vigencia !== "-";
                                return (
                                    <div key={item.iddocumento} className="crm-expediente-digital-fila">
                                        <div className="crm-expediente-digital-fila-info">
                                            <DescriptionOutlinedIcon className="crm-expediente-digital-icono-doc" />
                                            <span className="crm-expediente-digital-nombre">{capitalizeWords(item.documento)}</span>
                                        </div>
                                        <div className="crm-expediente-digital-fila-datos">
                                            <div className="crm-expediente-digital-fila-meta">
                                                {tieneFecha && <span className="crm-expediente-digital-fecha">{item.vigencia}</span>}
                                                <span
                                                    className={`crm-expediente-digital-badge${estado.modificador ? ` crm-expediente-digital-badge-${estado.modificador}` : ""}`}
                                                >
                                                    {estado.texto}
                                                </span>
                                            </div>
                                            <div className="crm-expediente-digital-fila-acciones">
                                                <button
                                                    type="button"
                                                    className="crm-expediente-digital-icono-btn"
                                                    title="Actualizar"
                                                    onClick={() => setDocumentoModal(item)}
                                                >
                                                    <RestartAltIcon />
                                                </button>
                                                {item.idclientedocumento > 0 && (
                                                    <button
                                                        type="button"
                                                        className="crm-expediente-digital-icono-btn"
                                                        title="Descargar"
                                                        onClick={() => onDescargar(item.idclientedocumento)}
                                                    >
                                                        <CloudDownloadOutlinedIcon />
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })
                        )}
                    </div>
                </div>
            </PlantillaScreen>

            <CRM_ModalActualizarDocumento
                open={documentoModal !== null}
                onClose={() => setDocumentoModal(null)}
                documento={documentoModal}
                idcliente={idcliente}
                onGuardado={cargarDocumentos}
            />
        </>
    );
};
